import TagInput from "/WebInterface/new-ui/modules/tag-input/tag-input.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";
import {
  verifyEmail,
  verifyEmailToken,
} from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import { growl } from "/WebInterface/new-ui/modules/toast.js";
import Modal from "/WebInterface/new-ui/modules/modal.js";
import Loader from "/WebInterface/new-ui/modules/loader/loader.js";

let pendingApiCalls = 0;
let formLoader = null;

const showLoader = () => {
  if (!formLoader) {
    formLoader = new Loader({});
  }
};

const hideLoader = () => {
  if (formLoader && pendingApiCalls === 0) {
    formLoader.deleteLoader();
    formLoader = null;
  }
};

const makeLookupFormFieldCall = ({ formName, formElementName }) => {
  pendingApiCalls++;
  showLoader();

  return ajax
    .call({
      method: "GET",
      urlEncoded: true,
      data: {
        command: "lookup_form_field",
        form_name: formName,
        form_element_name: formElementName,
      },
    })
    .always(() => {
      pendingApiCalls--;
      hideLoader();
    });
};

/**
 * Formats file list for display: shows up to 5 files, then "and x more..." if there are more.
 * @param {Array<string>} files - Array of file paths or filenames.
 * @returns {string} - Formatted HTML string with files separated by <br> tags.
 */
const formatFilesList = (files) => {
  console.log("formatFilesList - Input:", files);

  if (!files || !Array.isArray(files) || files.length === 0) {
    console.log(
      "formatFilesList - No files or empty array, returning placeholder"
    );
    return "[Files]";
  }

  // Extract filenames from paths
  const fileNames = files.map((file) => {
    return file;
  });
  console.log("formatFilesList - Extracted filenames:", fileNames);

  // Show up to 5 files without truncation
  const maxDisplay = 5;
  const filesToShow = fileNames.slice(0, maxDisplay);
  const remainingCount = fileNames.length - maxDisplay;
  console.log(
    "formatFilesList - Files to show:",
    filesToShow,
    "Remaining count:",
    remainingCount
  );

  let formattedList = filesToShow.join("<br>");

  // If there are more than 5 files, add "and x more..."
  if (remainingCount > 0) {
    formattedList += `<br>and ${remainingCount} more...`;
  }

  console.log("formatFilesList - Final formatted list:", formattedList);
  return formattedList;
};

/**
 * Replaces variables in text using a replacement array (similar to sharing.js approach).
 * @param {string} text - The text containing variables to replace.
 * @param {Array<Object>} replacements - Array of replacement objects with varToReplace and value.
 * @returns {string} - The text with variables replaced.
 */
const replaceLabelVariables = (text, replacements = []) => {
  if (!text || typeof text !== "string") {
    return text;
  }

  if (!Array.isArray(replacements) || replacements.length === 0) {
    return text;
  }

  let replacedText = text;
  replacements.forEach((replace) => {
    if (replace.varToReplace && replace.value !== undefined) {
      replacedText = replacedText.replace(
        new RegExp(replace.varToReplace, "g"),
        replace.value
      );
    }
  });

  return replacedText;
};

/**
 * Generates HTML markup for a form field based on the provided formField object.
 * @param {Object} formField - The form field object containing the field properties.
 * @param {string} formName - The name of the form.
 * @param {Object} options - Optional object containing additional data.
 * @param {Array<Object>} options.replacements - Array of variable replacement objects.
 * @param {Object} options.config - Form configuration object from window.customFormSettings.
 * @returns {string} - The generated HTML markup for the form field.
 */
const generateFormField = (formField, formName, options = {}) => {
  // Extracting properties from formField object
  const {
    lookup_type,
    required,
    label,
    value,
    name,
    type,
    size,
    max_chars,
    cols,
    rows,
    options: fieldOptions,
  } = formField;

  const { replacements = [], config = {} } = options;

  // Get label alignment from config (only for input types, not label type)
  // Map alignment values to CSS classes
  const labelAlignmentClass = config.label_alignment
    ? `has-text-${config.label_alignment}`
    : "";

  let html = "";
  let requiredField =
    required === "true" ? "<span class='required-field'>*</span>" : "";
  const randomId = utils.getUId();
  const encodedLabel = replaceLabelVariables(
    utils.sanitizePure(label),
    replacements
  );
  const encodedValue = typeof value === "string" ? utils.textEncode(value) : "";
  const encodedName = utils.sanitizePure(name);

  // Generating HTML based on the type of form field
  switch (type) {
    case "label": {
      const rightSideLabel = replaceLabelVariables(
        utils.sanitizePure(value),
        replacements
      );

      // Generating label HTML markup
      let u_name_label = `label_${randomId}`;
      if (encodedName) u_name_label = encodedName;

      let rightPage = null;
      let leftPage = null;
      const indexOfGETURLRight = rightSideLabel.indexOf("{get:");

      if (indexOfGETURLRight > -1) {
        const indexOfEndGETURL = rightSideLabel.indexOf("}");
        rightPage = rightSideLabel.substring(
          indexOfGETURLRight + 5,
          indexOfEndGETURL
        );
      }

      const indexOfGETURLLeft = encodedLabel.indexOf("{get:");
      if (indexOfGETURLLeft > -1) {
        const indexOfEndGETURL = encodedLabel.indexOf("}");
        leftPage = encodedLabel.substring(
          indexOfGETURLLeft + 5,
          indexOfEndGETURL
        );
      }

      html += `
        <div class="custom-form-field py-2  ${labelAlignmentClass} ${
          !rightSideLabel || leftPage ? " full-span" : ""
        }${leftPage ? " loadPage" : ""}" ${leftPage ? ` page='${leftPage}'` : ""}>
          <label class="text-primaryMain" name="meta_${u_name_label}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>`;

      if (rightSideLabel) {
        // Generating value label HTML markup if value is provided
        html += `
          <div class="custom-form-field py-2${
            rightPage ? " loadPage full-span" : ""
          }" ${rightPage ? ` page='${rightPage}'` : ""}>
            <label class="text-primaryMain" name="meta_${u_name_label}">
              ${rightSideLabel}
            </label>
          </div>`;
      }
      break;
    }

    case "text": {
      // Generating text input HTML markup
      let inputType = "text";
      let validateType = "required";
      const inputSize = utils.textEncode(size);
      const inputMaxChars = utils.textEncode(max_chars);
      // Store width in data attribute for CSP compliance, apply via JS after creation
      const inputWidthData = inputSize
        ? inputSize.toString().indexOf("%") > 0
          ? `data-width="${inputSize.toString()}"`
          : `data-width="${inputSize.toString().split("px")[0]}px"`
        : "";

      // Checking for specific input types based on the name
      if (
        encodedName.lastIndexOf("_password") >= 0 ||
        encodedName.lastIndexOf("password") >= 0
      ) {
        inputType = "password";
      }

      if (
        encodedName.lastIndexOf("_email") >= 0 ||
        encodedName.lastIndexOf("email") >= 0
      ) {
        validateType = "email";
      }

      if (
        encodedName.lastIndexOf("_date1") >= 0 ||
        encodedName.lastIndexOf("_date") >= 0
      ) {
        inputType = "date";
      }

      if (
        encodedName.lastIndexOf("_email_verify") >= 0 ||
        encodedName.lastIndexOf("email_verify") >= 0
      ) {
        html += `
            <div class="custom-form-field full-span">
              <div id="verify-email-container" data-validate-verified="false" class="is-flex is-flex-direction-column is-align-items-start">
                <div id="email-verified-error-message" class="error text-sm is-hidden" data-i18n-key="emailMustBeVerified">Email must be verified</div>
                <button class="primary-button px-2 py-1" type="button" id="custom-form-email-verify" data-i18n-key="verifyEmail">Verify Email</button>
              </div>
            </div>`;
        break;
      }

      // Check if this is a date input and if we need calendar icon or quick date picker
      const isDateInput = inputType === "date";
      const showCalendarIcon = isDateInput && config.show_calendar_icon;
      const showQuickDatePicker = isDateInput && config.show_quick_date_picker;
      const dateInputId = `custom-form-date-${encodedName}`;
      const quickDateInputId = `custom-form-quick-date-${encodedName}`;

      // Calculate date input width (increase by 10% if calendar icon or quick date picker is shown)
      // Store in data attribute for CSP compliance, apply via JS after creation
      let dateInputWidthData = inputWidthData;
      if (isDateInput && (showCalendarIcon || showQuickDatePicker)) {
        if (inputSize) {
          // If there's a size, increase it by 10%
          if (inputSize.toString().indexOf("%") > 0) {
            const percentValue = parseFloat(inputSize);
            dateInputWidthData = `data-width="${percentValue * 1.1}%"`;
          } else {
            const pxValue = parseFloat(inputSize.split("px")[0]);
            dateInputWidthData = `data-width="${pxValue * 1.1}px"`;
          }
        } else {
          // Default width increase
          dateInputWidthData = `data-width="110%"`;
        }
      }

      html += `
        <div class="custom-form-field py-2 ${labelAlignmentClass}">
          <label class="has-text-weight-semibold text-primaryMain" for="meta_${encodedName}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>
        <div class="custom-form-field gap-0-5rem ${
          showCalendarIcon || showQuickDatePicker
            ? "is-flex is-align-items-center"
            : ""
        }">
          ${
            showQuickDatePicker
              ? `
          <div class="flex-0-0-auto w-63px">
            <input type="number" id="${quickDateInputId}" class="input w-100 fs-0-875rem p-4px-8px" placeholder="Days" min="0" />
          </div>
          `
              : ""
          }
          <div class="${showQuickDatePicker ? "flex-1" : ""} ${
            showCalendarIcon ? "is-flex is-align-items-center gap-0-5rem" : ""
          }">
            <input id='${dateInputId}' data-form-name="${formName}" class="input" name="meta_${encodedName}" data-form-element-name="${encodedName}" type="${inputType}" ${
              dateInputWidthData || inputWidthData
            }
              ${
                inputType === "date" && encodedName.lastIndexOf("_date1") >= 0
                  ? "crushftp-future-date='true'"
                  : ""
              }
              ${value ? `value="${encodedValue}"` : ""}
              ${inputMaxChars ? `maxlength="${inputMaxChars}"` : ""}
              ${
                requiredField
                  ? `data-validate="${validateType}" data-mark-error="true" data-inline-error="true"`
                  : ""
              }
            />
            ${
              showCalendarIcon
                ? `
            <i class="icon-calendar is-cursor-pointer flex-shrink-0" data-date-input-id="${dateInputId}"></i>
            `
                : ""
            }
          </div>
        </div>`;
      break;
    }

    case "textarea": {
      // Generating textarea HTML markup
      let textareaCols = parseInt(cols);
      if (textareaCols > 40) textareaCols = 40;

      html += `
        <div class="custom-form-field py-2 ${labelAlignmentClass}">
          <label class="has-text-weight-semibold text-primaryMain" for="meta_${encodedName}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>
        <div class="custom-form-field">
          <textarea class="textarea" name="meta_${encodedName}" cols="${textareaCols}" rows="${rows}"
            ${
              requiredField
                ? 'data-validate="required" data-mark-error="true" data-inline-error="true"'
                : ""
            }>${encodedValue}</textarea>
        </div>`;
      break;
    }

    case "combo": {
      // Generating combo box HTML markup
      let comboOptions = fieldOptions?.options_subitem;
      let comboOptionsHTML = "";

      if (!Array.isArray(comboOptions)) {
        comboOptions = [comboOptions];
      }

      comboOptions
        .filter((o) => o !== undefined)
        .forEach((option) => {
          let value = "";
          let label = "";
          const splittedValue = option.split(":");
          if (splittedValue.length > 1) {
            label = splittedValue[0];
            value = splittedValue[1];
          } else {
            label = splittedValue[0];
            value = splittedValue[0];
          }
          comboOptionsHTML += `<option value="${value}">${label}</option>`;
        });

      html += `
        <div class="custom-form-field py-2 ${labelAlignmentClass}">
          <label class="has-text-weight-semibold text-primaryMain" for="meta_${encodedName}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>
        <div class="custom-form-field">
          <div class="select">
            <select name="meta_${encodedName}" class="primary-select" data-form-name="${formName}" data-form-element-name="${encodedName}" ${
              lookup_type ? `data-lookup-type="${lookup_type}"` : ""
            }>
              ${comboOptionsHTML}
            </select>
          </div>
        </div>`;
      break;
    }

    case "checkbox": {
      // Generating checkbox group HTML markup
      let checkboxOptions = fieldOptions?.options_subitem;
      let checkboxOptionsHTML = "";

      if (checkboxOptions === undefined) {
        checkboxOptions = [];
      } else if (!Array.isArray(checkboxOptions)) {
        checkboxOptions = [checkboxOptions];
      }

      checkboxOptions
        .filter((o) => o !== undefined)
        .forEach((option) => {
          const nameValuePair = option.split(":");
          let selected = "";
          let label = "";
          let value = "";

          if (nameValuePair.length === 1) {
            label = nameValuePair[0];
            value = nameValuePair[0];
          } else if (nameValuePair.length === 2) {
            label = nameValuePair[0];
            value = nameValuePair[1];
          } else if (nameValuePair.length === 3) {
            label = nameValuePair[0];
            value = nameValuePair[1];
            if (value === nameValuePair[2]) {
              selected = "checked";
            }
          }

          checkboxOptionsHTML += `
            <div class="is-flex is-align-items-center py-2 ${
              checkboxOptions.length === 1 && !label ? "py-3" : ""
            }">
              <input id="meta_${encodedName}_option_${option}" ${
                !label && checkboxOptions.length === 1
                  ? "data-single-checkbox"
                  : ""
              }  ignore_serialization="true" name="meta_${encodedName}" type="checkbox" ${selected} value="${value}" />
              <label class="text-sm text-primaryMain px-2" for="meta_${encodedName}_option_${option}">${label}</label>
            </div>`;
        });

      html += `
        <div class="custom-form-field py-2 ${labelAlignmentClass}">
          <label class="has-text-weight-semibold text-primaryMain">${encodedLabel} ${requiredField}</label>
        </div>
        <div class="custom-form-field">
          <div ${
            requiredField
              ? 'data-validate="checkbox-group" data-mark-error="true" data-inline-error="true"'
              : ""
          } class="py-1 is-flex is-flex-wrap-wrap checkbox-parent" ${
            lookup_type ? `data-lookup-type="${lookup_type}"` : ""
          } data-form-name="${formName}" data-form-element-name="${encodedName}" data-encoded-name="${encodedName}" id="crushcheck_${name}">
            ${checkboxOptionsHTML}
          </div>
        </div>`;
      break;
    }

    case "tags": {
      // Generating tags input HTML markup
      let tagsOptions = fieldOptions?.options_subitem;
      if (tagsOptions === undefined) {
        tagsOptions = [];
      } else if (!Array.isArray(tagsOptions)) {
        tagsOptions = [tagsOptions];
      }
      tagsOptions = tagsOptions.filter((o) => o !== undefined);
      const hasCustomInName = name.toLowerCase().includes("custom");
      html += `
        <div class="custom-form-field py-2 ${labelAlignmentClass}">
          <label class="has-text-weight-semibold text-primaryMain" for="meta_${name}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>
        <div class="custom-form-field">
          <w-autocomplete-input
            name="meta_${encodedName}"
            options="${tagsOptions.join(",")}"
            data-parent=".modal .modal-card-body"
            ${hasCustomInName ? 'enable-custom="true"' : ""}
            ${
              requiredField
                ? 'data-validate="required" data-mark-error="true" data-inline-error="true"'
                : ""
            }
          ></w-autocomplete-input>
        </div>`;
      break;
    }

    case "radio": {
      // Generating radio button group HTML markup
      let radioOptions = fieldOptions?.options_subitem;
      let radioOptionsHTML = "";

      if (radioOptions === undefined) {
        radioOptions = [];
      } else if (!Array.isArray(radioOptions)) {
        radioOptions = [radioOptions];
      }
      radioOptions = radioOptions.filter((o) => o !== undefined);
      radioOptions.forEach((option, index) => {
        const nameValuePair = option.split(":");
        let selected = index == 0 ? "checked" : "";
        let label = "";
        let value = "";

        if (nameValuePair.length === 1) {
          label = nameValuePair[0];
          value = nameValuePair[0];
        } else if (nameValuePair.length === 2) {
          label = nameValuePair[0];
          value = nameValuePair[1];
        } else if (nameValuePair.length === 3) {
          label = nameValuePair[0];
          value = nameValuePair[1];
          if (value === nameValuePair[2]) {
            selected = "checked";
          }
        }

        radioOptionsHTML += `
          <div class="is-flex is-align-items-center">
            <input type="radio" name="meta_${encodedName}" id="meta_${encodedName}_option_${option}" ${selected} value="${value}" />
            <label for="meta_${encodedName}_option_${option}" class="text-sm text-primaryMain px-2">${label}</label>
          </div>`;
      });

      html += `
        <div class="custom-form-field py-2 ${labelAlignmentClass}">
          <label class="has-text-weight-semibold text-primaryMain">${encodedLabel} ${requiredField}</label>
        </div>
        <div class="custom-form-field py-2 is-flex is-flex-wrap-wrap" ${
          lookup_type ? `data-lookup-type="${lookup_type}"` : ""
        } data-form-name="${formName}" data-form-element-name="${encodedName}" data-encoded-name="${encodedName}" id="crushradio_${name}">
          ${radioOptionsHTML}
        </div>`;
      break;
    }

    default:
      break;
  }

  return html;
};

/**
 * Checks if files are required and selected before opening custom form.
 * @param {string} formName - The name of the form.
 * @param {Array<string>|string} paths - The paths to check.
 * @returns {boolean} - Returns true if files are not required or files are provided, false otherwise.
 */
const checkFilesRequired = (formName, paths, config = null) => {
  // Use provided config, or fallback to window config
  const formConfig = config || window.customFormSettings?.[formName];
  if (!formConfig?.files_required) {
    return true; // Files not required, allow form to open
  }

  // Check if paths are provided
  if (!paths) {
    return false;
  }

  // Convert paths to array to check if any exist
  let pathsArray = [];
  if (typeof paths === "string") {
    if (paths.includes("|")) {
      pathsArray = paths
        .split("|")
        .filter((path) => path && path.trim().length > 0);
    } else {
      pathsArray = paths
        .split(/\r?\n/)
        .filter((path) => path && path.trim().length > 0);
    }
  } else if (Array.isArray(paths)) {
    pathsArray = paths.filter(
      (path) =>
        path && (typeof path === "string" ? path.trim().length > 0 : true)
    );
  }

  return pathsArray.length > 0;
};

/**
 * Generates a custom form based on the provided form information.
 * @param {object} formInfo - The form information containing the entries and their properties.
 * @param {Object} options - Optional object containing additional data.
 * @param {Array<Object>} options.variables - Array of variable replacement objects with varToReplace and value (like sharing.js).
 * @param {Array<string>|string} options.paths - Array of file paths or string with paths (will be converted to {files} replacement).
 * @returns {string} The generated HTML content for the custom form.
 */
const generateCustomForm = (formInfo, options = {}) => {
  const formName = formInfo.name;
  let formFields = formInfo.entries.entries_subitem;

  if (!Array.isArray(formFields)) {
    formFields = [formFields];
  }

  // Get form configuration: use provided config from options, or fallback to window.customFormSettings
  const config = options.config || window.customFormSettings?.[formName] || {};

  // Build variable replacements array (similar to sharing.js approach)
  let replacements = options.variables || [];

  // If paths are provided, convert them to {files} replacement
  if (options.paths) {
    let pathsArray = [];
    const paths = options.paths;

    if (typeof paths === "string") {
      // Try splitting by | first (common separator), then by newlines
      if (paths.includes("|")) {
        pathsArray = paths
          .split("|")
          .filter((path) => path && path.trim().length > 0);
      } else {
        pathsArray = paths
          .split(/\r?\n/)
          .filter((path) => path && path.trim().length > 0);
      }
    } else if (Array.isArray(paths)) {
      pathsArray = paths.filter(
        (path) =>
          path && (typeof path === "string" ? path.trim().length > 0 : true)
      );
    }

    // Extract filenames and format like sharing.js (up to 5 files, then "and x more...")
    if (pathsArray.length > 0) {
      const fileNames = pathsArray.map((path) => {
        return path;
      });

      // Format files: show up to 5, then "and x more..."
      const maxDisplay = 5;
      const filesToShow = fileNames.slice(0, maxDisplay);
      const remainingCount = fileNames.length - maxDisplay;

      let filesFormatted = filesToShow.join("<br>");
      if (remainingCount > 0) {
        filesFormatted += `<br>and ${remainingCount} more...`;
      }

      // Add {files} replacement to the array
      replacements.push({
        varToReplace: "{files}",
        value: filesFormatted,
      });
    }
  }

  let htmlContent = `
    <div class="is-flex is-flex-direction-column is-align-items-stretch custom-form w-full">
      <form class="grid-column-gap w-full">`;

  formFields.forEach((fieldInfo) => {
    htmlContent += generateFormField(fieldInfo, formName, {
      replacements,
      config,
    });
  });

  htmlContent += `
      </form>
    </div>`;

  return htmlContent;
};

/**
 * Attaches date picker functionality to input elements with type "date" within the specified parent.
 * @param {string|HTMLElement} parent - The parent element or selector within which to attach the date picker.
 * @param {string} formName - The name of the form.
 * @param {Object} config - Optional form configuration object (takes precedence over window config).
 */
const attachDatePicker = (
  parent = document.body,
  formName = "",
  config = null
) => {
  let parentElement = parent;

  const customDateFormat = utils.getUserCustomizationValue(
    "DATE_FORMAT_TEXT",
    true
  );

  let dateFormat = null;
  if (customDateFormat) {
    dateFormat = utils.dateStringFormat({ format: customDateFormat });
  }

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  // Get form configuration: use provided config, or fallback to window.customFormSettings
  const formConfig = config || window.customFormSettings?.[formName] || {};

  const inputDateType = parentElement.querySelectorAll('[type="date"]');

  inputDateType.forEach((element) => {
    const isFutureDate = element.getAttribute("crushftp-future-date");
    const dateInputId = element.id;
    const quickDateInputId = `custom-form-quick-date-${dateInputId.replace(
      "custom-form-date-",
      ""
    )}`;
    const quickDateInput = parentElement.querySelector(`#${quickDateInputId}`);

    let options = {
      dateFormat: dateFormat || "m/d/Y",
    };

    if (isFutureDate) {
      const date = new Date();
      date.setDate(date.getDate() + 1);

      options = { ...options, minDate: date };
    }

    const calendar = flatpickr(`#${dateInputId}`, options);
    const value = element.value;

    if (value) {
      const date = new Date(value);
      calendar.setDate(date);
    }

    // Attach calendar icon click handler if enabled
    if (formConfig.show_calendar_icon) {
      const calendarIcon = parentElement.querySelector(
        `[data-date-input-id="${dateInputId}"]`
      );
      if (calendarIcon) {
        calendarIcon.addEventListener("click", () => {
          calendar.open();
        });
      }
    }

    // Attach quick date picker if enabled
    if (formConfig.show_quick_date_picker && quickDateInput) {
      // Flag to prevent infinite loops during sync
      let isUpdatingFromDays = false;
      let isUpdatingFromCalendar = false;

      // Helper function to calculate days from today to a given date
      const calculateDaysFromToday = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const targetDate = new Date(date);
        targetDate.setHours(0, 0, 0, 0);
        const diffTime = targetDate - today;
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
      };

      // Helper function to get date X days from today
      const getDateFromDays = (days) => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() + Number(days));
        return date;
      };

      // Update days input when calendar date changes
      const updateDaysFromCalendar = (selectedDates) => {
        if (isUpdatingFromDays) return; // Prevent loop

        if (selectedDates && selectedDates.length > 0) {
          isUpdatingFromCalendar = true;
          const selectedDate = selectedDates[0];
          const diffDays = calculateDaysFromToday(selectedDate);
          if (diffDays >= 0) {
            quickDateInput.value = diffDays;
          } else {
            quickDateInput.value = "";
          }
          // Use setTimeout to reset flag after DOM update
          setTimeout(() => {
            isUpdatingFromCalendar = false;
          }, 0);
        } else {
          quickDateInput.value = "";
        }
      };

      // Add onChange handler to Flatpickr
      if (!Array.isArray(calendar.config.onChange)) {
        calendar.config.onChange = calendar.config.onChange
          ? [calendar.config.onChange]
          : [];
      }
      calendar.config.onChange.push(updateDaysFromCalendar);

      // Update calendar when days input changes
      quickDateInput.addEventListener("input", (e) => {
        if (isUpdatingFromCalendar) return; // Prevent loop

        const days = parseInt(e.target.value, 10);
        if (!isNaN(days) && days >= 0) {
          isUpdatingFromDays = true;
          const targetDate = getDateFromDays(days);
          calendar.setDate(targetDate);
          // Reset flag after a brief delay to allow onChange to complete
          setTimeout(() => {
            isUpdatingFromDays = false;
          }, 10);
        } else if (e.target.value === "") {
          isUpdatingFromDays = true;
          calendar.clear();
          quickDateInput.value = "";
          setTimeout(() => {
            isUpdatingFromDays = false;
          }, 10);
        }
      });

      // Sync initial value if calendar already has a date
      setTimeout(() => {
        if (calendar.selectedDates && calendar.selectedDates.length > 0) {
          updateDaysFromCalendar(calendar.selectedDates);
        }
      }, 100);
    }
  });
};

const applyCascadeOnForm = (parent = document.body) => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  parentElement = _$(parentElement);

  parentElement.find("[name*='_cascade']").each((element) => {
    const cascadeElement = _$(element);
    cascadeElement.on("change", () => {
      let optionList = [];
      let val = cascadeElement.val();
      const name = cascadeElement.attr("name");

      if (cascadeElement.is("select")) {
        if (!Array.isArray(val)) val = [val];

        cascadeElement.find("option").each((option) => {
          parentElement
            .find(
              `${`*[name$='${_$(option).attr("value")}_not']`}, ${`*[for$='${_$(
                option
              ).attr("value")}_not']`}`
            )
            .each((option) => {
              _$(option)
                .selfOrParent(".custom-form-field")
                .classList.remove("is-hidden");

              if (
                (_$(option).attr("name")?.indexOf("_cascade") > 0 &&
                  _$(option).attr("name") != name) ||
                (_$(option).attr("for")?.indexOf("_cascade") > 0 &&
                  _$(option).attr("for") != name)
              ) {
                optionList.push(option);
              }
            });
        });

        cascadeElement.find("option").each((option) => {
          const regex = new RegExp(`_${_$(option).val()}(_.*)?$`);

          parentElement.find("*").each((option) => {
            const nameAttr = option.getAttribute("name");
            const forAttr = option.getAttribute("for");

            if (
              (nameAttr && regex.test(nameAttr)) ||
              (forAttr && regex.test(forAttr))
            ) {
              _$(option)
                .selfOrParent(".custom-form-field")
                .classList.add("is-hidden");
            }
          });
        });

        for (let i = 0; i < val.length; i++) {
          const curVal = val[i];
          const regex = new RegExp(`_${curVal}(_.*)?$`);

          parentElement.find("*").each((option) => {
            const nameAttr = option.getAttribute("name");
            const forAttr = option.getAttribute("for");

            if (
              (nameAttr && regex.test(nameAttr)) ||
              (forAttr && regex.test(forAttr))
            ) {
              _$(option)
                .selfOrParent(".custom-form-field")
                .classList.remove("is-hidden");

              if (
                (nameAttr?.indexOf("_cascade") > 0 && nameAttr != name) ||
                (forAttr?.indexOf("_cascade") > 0 && forAttr != name)
              ) {
                optionList.push(element);
              }
            }
          });
        }

        for (var i = 0; i < optionList.length; i++) {
          optionList[i].dispatchEvent(new Event("change"));
        }

        for (let i = 0; i < val.length; i++) {
          let curVal = val[i];
          parentElement
            .find(
              `${`*[name$='_${curVal}_not']`}, ${`*[for$='_${curVal}_not']`}`
            )
            .each((option) => {
              _$(option)
                .selfOrParent(".custom-form-field")
                .classList.add("is-hidden");
            });
        }
      } else {
        parentElement
          .find(`[name="${cascadeElement.attr("name")}"]`)
          .each((ele) => {
            parentElement
              .find(
                `${`*[name$="_${_$(ele).attr("value")}"]`}, ${`*[for$="_${_$(
                  ele
                ).attr("value")}"]`}`
              )
              .each((option) => {
                _$(option)
                  .selfOrParent(".custom-form-field")
                  .classList.add("is-hidden");
              });

            parentElement
              .find(
                `${`*[name$="_${_$(ele).attr(
                  "value"
                )}_not"]`}, ${`*[for$="_${_$(ele).attr("value")}_not"]`}`
              )
              .each((option) => {
                _$(option)
                  .selfOrParent(".custom-form-field")
                  .classList.remove("is-hidden");
              });
          });

        parentElement
          .find(`[name="${cascadeElement.attr("name")}"]:checked`)
          .each((ele) => {
            parentElement
              .find(
                `${`*[name$="_${_$(ele).attr("value")}"]`}, ${`*[for$="_${_$(
                  ele
                ).attr("value")}"]`}`
              )
              .each((option) => {
                _$(option)
                  .selfOrParent(".custom-form-field")
                  .classList.remove("is-hidden");
              });

            parentElement
              .find(
                `${`*[name$="_${_$(ele).attr(
                  "value"
                )}_not"]`}, ${`*[for$="_${_$(ele).attr("value")}_not"]`}`
              )
              .each((option) => {
                _$(option)
                  .selfOrParent(".custom-form-field")
                  .classList.add("is-hidden");
              });
          });
      }
    });
    cascadeElement.el().dispatchEvent(new Event("change"));
  });

  parentElement.find("input").each((element) => {
    const name = element.name;
    if (
      name.indexOf("verify") >= 0 &&
      parentElement.find(`input[name='${name}2']`).length > 0
    ) {
      parentElement
        .find(`input[name='${name}2']`)
        .attr("data-validate", "match")
        .attr("data-mark-error", "true")
        .attr("data-inline-error", "true")
        .attr("data-match", name);
    }
  });
};

const attachComboDBInputs = (parent = document.body) => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  parentElement = _$(parentElement);

  parentElement.find("select[name*='_db']").each((element) => {
    const formName = element.getAttribute("data-form-name");
    const formElementName = element.getAttribute("data-form-element-name");
    makeLookupFormFieldCall({
      formName,
      formElementName,
    }).then((res) => {
      setTimeout(() => {
        const optionsArray = res?.map((item) => {
          return { value: item.id, label: item.name };
        });
        element.innerHTML = "";
        optionsArray.forEach((optionValue) => {
          const option = document.createElement("option");
          option.value = optionValue.value;
          option.textContent = optionValue.label;
          element.appendChild(option);
        });

        applyCascadeOnForm(parentElement);
      }, 500);
    });
  });
};

const attachCheckboxDBInputs = (parent = document.body) => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  parentElement = _$(parentElement);

  parentElement.find("div[id^='crushcheck_'][id$='_db']").each((element) => {
    const dataEncodedName = element.getAttribute("data-encoded-name");
    const formName = element.getAttribute("data-form-name");
    const formElementName = element.getAttribute("data-form-element-name");

    makeLookupFormFieldCall({
      formName,
      formElementName,
    }).then((res) => {
      const optionsArray = res?.map((item) => {
        return { value: item.id, label: item.name };
      });
      element.innerHTML = "";
      optionsArray.forEach((optionValue) => {
        const value = optionValue.value;
        const label = optionValue.label;
        const optionId = `meta_${dataEncodedName}_option_${value}`;
        const html = `
            <div class="is-flex is-align-items-center">
              <input id="${optionId}" ignore_serialization="true" name="meta_${dataEncodedName}" type="checkbox" value="${value}" />
              <label class="text-sm text-primaryMain px-2" for="${optionId}">${label}</label>
            </div>
          `;
        element.insertAdjacentHTML("beforeend", html);
      });

      applyCascadeOnForm(parentElement);
    });
  });
};

const attachRadioDBInputs = (parent = document.body) => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  parentElement = _$(parentElement);

  parentElement.find("div[id^='crushradio_'][id$='_db']").each((element) => {
    const dataEncodedName = element.getAttribute("data-encoded-name");
    const formName = element.getAttribute("data-form-name");
    const formElementName = element.getAttribute("data-form-element-name");

    makeLookupFormFieldCall({
      formName,
      formElementName,
    }).then((res) => {
      const optionsArray = res?.map((item) => {
        return { value: item.id, label: item.name };
      });
      element.innerHTML = "";
      optionsArray.forEach((optionValue) => {
        const value = optionValue.value;
        const label = optionValue.label;
        const optionId = `meta_${dataEncodedName}_option_${value}`;
        const html = `
            <div class="is-flex is-align-items-center">
              <input id="${optionId}" name="meta_${dataEncodedName}" type="radio" value="${value}" />
              <label class="text-sm text-primaryMain px-2" for="${optionId}">${label}</label>
            </div>
          `;
        element.insertAdjacentHTML("beforeend", html);
      });

      applyCascadeOnForm(parentElement);
    });
  });
};

const attachTagInputs = (parent = document.body, formName = "") => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  // Add form submit handler
  const form = parentElement.querySelector("form");
  if (form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const modal = parentElement.closest(".modal");
      if (modal) {
        const submitBtn = modal.querySelector(
          ".modal-card-foot button.primary-button"
        );
        if (submitBtn) {
          submitBtn.click();
        }
      }
    });
  }

  const textInputs = parentElement.querySelectorAll(
    'input[name*="_db"][type="text"]'
  );
  const tagInput = new TagInput();

  textInputs.forEach((element) => {
    const handleUsernameDebounce = utils.debounce((e) => {
      const value = e?.target?.value;
      if (!value) {
        tagInput.updateOptions({
          autoComplete: autoCompleteElement,
          helperText: t("typeInASearchTerm"),
        });
        return;
      }

      tagInput.updateOptions({
        autoComplete: autoCompleteElement,
        helperText: `${t("searching")}...`,
      });

      const formElementName = element.getAttribute("data-form-element-name");
      ajax
        .call({
          method: "GET",
          urlEncoded: true,
          data: {
            command: "lookup_form_field",
            form_name: formName,
            form_element_name: formElementName,
            q: value,
          },
        })
        .then((res) => {
          setTimeout(() => {
            const usernames = res?.map((item) => {
              return item.name;
            });
            const usernamesString = usernames.join();

            tagInput.updateOptions({
              autoComplete: autoCompleteElement,
              options: usernamesString || value,
            });
          }, 500);
        });
    }, 500);

    const autoCompleteElement = tagInput.attach({
      input: `#${element.id}`,
      helperText: t("typeInASearchTerm"),
      onInputChange: handleUsernameDebounce,
    });
  });
};

const applyVerifyEmailAndToken = (parent = document.body) => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  parentElement = _$(parentElement);

  const emailInput = Array.from(parentElement.find("input")).find(
    (input) =>
      input.id.lastIndexOf("_email") >= 0 || input.id.lastIndexOf("email") >= 0
  );
  const verifyEmailContainer = parentElement.find(
    "#verify-email-container",
    true
  );

  if (emailInput && verifyEmailContainer) {
    emailInput.insertAdjacentHTML(
      "afterend",
      '<p class="text-sm text-success is-hidden" id="custom-form-email-verified-text"><i class="icon-ok-circle"></i> <span data-i18n-key="verified">Verified</span></p>'
    );
    emailInput.insertAdjacentHTML(
      "afterend",
      '<p class="text-sm text-error is-hidden" id="custom-form-email-verified-failed-text"><i class="icon-remove-circle"></i> <span data-i18n-key="verificationFailed">Verification failed</span></p>'
    );
    const verifyButton = verifyEmailContainer.querySelector(
      "#custom-form-email-verify"
    );
    const verifyFailedText = parentElement.find(
      "#custom-form-email-verified-failed-text",
      true
    );
    const verifiedText = parentElement.find(
      "#custom-form-email-verified-text",
      true
    );

    const renderTokenVerificationPopup = () => {
      const tokenDialog = new Modal({
        title: "verifyToken",
        body: '<form><div class="control"><input class="input" name="email_token" type="text" data-validate="required" data-mark-error="true" data-inline-error="true" placeholder="Token"></div></form>',
        uniqueId: "verifyToken",
        closable: false,
        hasCloseOption: false,
        buttons: [
          {
            label: "cancel",
            close: true,
            classes: ["is-bordered", "mr-2"],
            onClick: () => {
              verifyButton.classList.remove("is-hidden");
              verifyEmailContainer.setAttribute(
                "data-validate-verified",
                "false"
              );
            },
          },
          {
            label: "ok",
            close: false,
            classes: [""],
            onClick: () => {
              const validateFields = validate(tokenDialog.root, true);
              if (validateFields.length === 0) {
                const formData = utils.serializeInputs(tokenDialog.root);
                const emailToken = formData?.email_token;
                if (emailToken) {
                  const verifyModalCardLoading = new Loader({
                    parentElement:
                      tokenDialog.root.querySelector(".modal-card"),
                  });
                  verifyEmailToken({
                    email: emailInput.value,
                    token: emailToken,
                  })
                    .then(({ commandResult }) => {
                      if (commandResult?.response === "FAILURE") {
                        growl(t("someThingWentWrong"), "", 5000, true);
                        verifyFailedText.classList.remove("is-hidden");
                        verifyButton.classList.remove("is-hidden");
                        verifiedText.classList.add("is-hidden");
                        verifyEmailContainer.setAttribute(
                          "data-validate-verified",
                          "false"
                        );
                        tokenDialog.hide(true);
                      } else if (commandResult?.response === "SUCCESS") {
                        growl(t("emailHasBeenVerifiedSuccessfully"), "", 5000);
                        verifyEmailContainer.setAttribute(
                          "data-validate-verified",
                          "true"
                        );
                        verifiedText.classList.remove("is-hidden");
                        verifyFailedText.classList.add("is-hidden");
                        verifyButton.classList.add("is-hidden");
                        tokenDialog.hide(true);
                      }
                      verifyEmailContainer.classList.remove("is-invalid");
                      verifyEmailContainer
                        .querySelector("#email-verified-error-message")
                        .classList.add("is-hidden");
                    })
                    .catch(() => {
                      growl(t("someThingWentWrong"), "", 5000, true);
                      verifiedText.classList.add("is-hidden");
                      verifyFailedText.classList.remove("is-hidden");
                      verifyEmailContainer.setAttribute(
                        "data-validate-verified",
                        "false"
                      );
                    })
                    .finally(() => verifyModalCardLoading.deleteLoader());
                } else {
                  growl(t("tokenIsRequiredForVerification"), "", 5000, true);
                }
              }
            },
          },
        ],
      });
    };

    verifyButton.addEventListener("click", () => {
      if (emailInput.value) {
        const modalCardLoading = new Loader({
          parentElement: parentElement.find(".modal-card").el(),
        });
        verifiedText.classList.add("is-hidden");
        verifyFailedText.classList.add("is-hidden");
        const errorWrapper = emailInput.nextElementSibling;
        if (errorWrapper?.querySelector(".error")) {
          errorWrapper.remove();
        }
        verifyEmail({ email: emailInput.value })
          .then(({ commandResult }) => {
            if (commandResult?.response === "Email sent.") {
              growl(commandResult?.response, "", 5000);
              verifyButton.classList.add("is-hidden");
              emailInput.classList.remove("is-invalid");

              verifyEmailContainer.classList.remove("is-invalid");
              verifyEmailContainer
                .querySelector("#email-verified-error-message")
                .classList.add("is-hidden");
              verifyEmailContainer.setAttribute(
                "data-validate-verified",
                "false"
              );

              renderTokenVerificationPopup();
            }
          })
          .catch(() => {
            verifyEmailContainer.classList.remove("is-invalid");
            verifyEmailContainer
              .querySelector("#email-verified-error-message")
              .classList.add("is-hidden");
            verifyEmailContainer.setAttribute(
              "data-validate-verified",
              "false"
            );
            growl(t("someThingWentWrong"), "", 5000, true);
          })
          .finally(() => modalCardLoading.deleteLoader());
      } else {
        growl(t("emailIsRequiredForVerification"), "", 5000, true);
        verifyEmailContainer.setAttribute("data-validate-verified", "false");
      }
    });
  } else if (verifyEmailContainer) {
    verifyEmailContainer?.closest(".custom-form-field")?.remove();
  }
};

const setFormFieldAttributes = (parent = document.body) => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  parentElement = _$(parentElement);

  parentElement.find("*[name$='_disabled']").attr("disabled", "disabled");
  parentElement.find("*[name$='_readonly']").attr("readonly", "readonly");
  parentElement.find("*[for$='_hidden']").each((element) => {
    const parentDiv = _$(element).selfOrParent(".custom-form-field");
    if (parentDiv) {
      const divElement = parentDiv.classList ? parentDiv : _$(parentDiv).el();
      if (divElement) {
        divElement.classList.add("is-hidden");
      }
    }
  });
  parentElement.find("*[name$='_hidden']").each((element) => {
    const parentDiv = _$(element).selfOrParent(".custom-form-field");
    if (parentDiv) {
      const divElement = parentDiv.classList ? parentDiv : _$(parentDiv).el();
      if (divElement) {
        divElement.classList.add("is-hidden");
      }
    }
  });
};

const attachCustomFormTools = (parentElement, formName, config = null) => {
  attachDatePicker(parentElement, formName, config);
  applyCascadeOnForm(parentElement);
  attachComboDBInputs(parentElement);
  attachCheckboxDBInputs(parentElement);
  attachRadioDBInputs(parentElement);
  attachTagInputs(parentElement, formName);
  applyVerifyEmailAndToken(parentElement);
  setFormFieldAttributes(parentElement);

  const firstInput = parentElement.querySelector("input");

  if (firstInput) {
    firstInput.focus();
  }

  // Load pages in customform
  _$(parentElement)
    .find(".loadPage")
    .sel()
    .forEach((el) => {
      const page = _$(el).attr("page");
      if (page) {
        _$(el).html("");

        const element = _$("<div>");
        element.addClass("has-text-left");
        _$(el).append(element.el());

        // NOTE: Setting style via attr() violates CSP.
        // This appears to be for custom page styling - consider using data attributes and CSS instead.
        if (page.indexOf("#") > 0) {
          let style = page.substring(page.indexOf("#") + 1, page.length);
          // Store in data attribute instead of inline style for CSP compliance
          element.attr("data-custom-style", style);
          console.warn(
            "Custom form: inline styles via #hash are deprecated. Use CSS classes instead."
          );
        }

        element.css("overflow", "auto");
        element.text("Wait..");

        ajax
          .call({
            method: "GET",
            url: page,
          })
          .then((response) => {
            element.html(response);
          })
          .catch((error) => {
            element.text("Error while loading: " + page);
          });
      }
    });
};

export { generateCustomForm, attachCustomFormTools, checkFilesRequired };
